import { getInstanceDefinition } from '../../cards/cardLookup';
import { computeHandGoldValue, getPlayerCollectionInstanceIds } from '../../state/selectors';
import { parseAlwaysActiveTraits, isHeroLike } from '../../cards/effectHelpers';
import { synergyScore } from './synergyClusters';

// Tunable knobs for pickBestBuy's scoring, exposed as an object (rather
// than inline constants) so the self-play tournament harness
// (scripts/aiTournament.mjs) - and eventually difficulty tiers - can
// experiment with different values without a second copy of this file.
export const DEFAULT_BUY_WEIGHTS = {
  // Matt reported this directly: "it just buys all the regulars." A $0
  // card's cardValue/cost ratio blows up purely from dividing by a tiny
  // number, regardless of whether the card is actually good - this floors
  // the divisor so a free/near-free card can't structurally dominate the
  // ratio just by being cheap.
  costFloor: 2,
  // Matt: "the number of cards of a specific type in a deck is an
  // important thing to vary - just because one card is the best card
  // doesn't mean you want all of them." Each already-owned copy of the
  // exact same card divides its score further: 0 owned -> no penalty,
  // 2 owned -> /2, 6 owned -> /4, smoothly rather than a hard cap.
  diminishingReturnsRate: 0.5,
  // Probability [0,1] that heuristicBot.js's pickBestBuyWithMistake ignores
  // the top-scored buy and either skips buying entirely or takes a random
  // affordable alternative instead - the AI-difficulty lever. costFloor/
  // diminishingReturnsRate alone can't produce a meaningfully weaker
  // "easy" AI: they only change WHICH card gets bought, while the bot's
  // actual competence (combat targeting, equip planning, top-level action
  // choice) is hardcoded and reads no weights at all - confirmed by
  // reading heuristicBot.js/dungeonHeuristics.js in full while designing
  // difficulty tiers. 0 (never mistake) preserves today's behavior exactly.
  mistakeChance: 0,
  // Matt: "what you want is just a handful of cards that work well
  // together." Multiplier applied to synergyClusters.js's synergyScore
  // (a card's bonus for reinforcing a combo the player is already
  // building, or being reinforced by one already owned) before adding it
  // to cardValue - see costEfficiency below, which only applies this at
  // all when state.meta.randomizedVillage is on.
  //
  // 0 (off) is the honest, evidence-based default - not a placeholder. A
  // first A/B sweep (yarn ai-tournament --randomized-village) on ONE
  // 300-600-game window found 0.5 looking like a real +8-11pp win-rate
  // improvement (that pass also caught and fixed a real bug: basic-torch/
  // basic-longspear - cheap Basic-trait cards that are also their own
  // cluster's resource - had reproduced the exact Regular/Longspear
  // structural-dominance pattern costFloor/isHeroLike already fixed once,
  // via a new path; fixed by excluding Basic-trait cards from every
  // cluster, see isBasic/EXCLUDE_BASIC). But re-sampling the SAME 0.5
  // across 4 more independent seed windows (seed-start 1000/3000/5000/
  // 9000, 300 games each) came back 51.0% / 45.7% / 52.1% / 49.3% - the
  // first result was a favorable-window artifact, not a real effect.
  // Confirmed at a genuinely large sample (1500 games): 50.6%, i.e. noise.
  // Isolating individual clusters (hero-count alone, disease-count+xp-total
  // alone - the two most textually-grounded, self-reinforcement-free
  // clusters) against a properly-explicit synergyWeight: 0 baseline also
  // came back flat (49.0-50.6%) at 900 games each. (An earlier attempt at
  // this isolation test looked like a real +2-6.5pp signal for hero-count,
  // but that was ANOTHER methodology bug: this constant had already been
  // set to 0.5 at that point, so `--weights-b='{}'` silently inherited 0.5
  // too instead of meaning "off" - a "0.5 vs 0.5" comparison, not a real
  // one. Caught by a control run with genuinely identical weights on both
  // sides, which showed the same ~1pp noise floor.)
  //
  // UPDATE after building scripts/aiRoundRobin.mjs (8-strategy round robin,
  // 400 games/matchup across 4 independent seed windows, randomizedVillage
  // - a much more rigorous check than the single-pair A/B sweeps above):
  // plain `synergy` (synergyWeight: 0.5, default clusterCap) lost to
  // `baseline` (this exact 0-weight default) 43.7% to 56.3%, CONSISTENTLY
  // across all 4 windows (57/56/56/57% baseline win rate - unusually low
  // variance for this harness, i.e. a genuinely confident result, not
  // noise). So the honest finding sharpened from "neutral" to "a real,
  // mild net negative" once tested against a strong baseline instead of
  // against itself. `synergy-tight` (clusterCap: 3) and `synergy-loose`
  // (clusterCap: 8) both outperformed plain `synergy` (55.0%/54.7% overall
  // vs 52.8%), and synergy-tight was the only synergy-weighted variant to
  // (barely, noisily) beat baseline head-to-head - not strong enough to
  // adopt, but a hint that a different clusterCap might matter more than
  // synergyWeight's magnitude alone, worth a future dedicated clusterCap
  // sweep.
  //
  // Net conclusion at the time of THIS finding: the synergy-scoring
  // MECHANISM is real and verified - it does change buying behavior (see
  // synergyClusters.test.js) - but the FULL cluster set turned on together
  // produces no reproducible win-rate improvement, and looks like a mild
  // net negative. UPDATE: a later, more targeted pass (isolating each
  // cluster individually rather than judging the whole mixed bag) found
  // one real exception - see DIFFICULTY_PRESETS.hard's own comment for the
  // polearm-count finding it's now built from. 0 stays the default here
  // because that finding is specific (one cluster, one weight, validated
  // for `hard`), not a case for turning synergy on broadly by default.
  synergyWeight: 0,
  // Matt: "number of cards in the synergy" - passed to synergyClusters.js's
  // synergyScore as the point where a cluster's counter stops adding more
  // bonus (see that file's DEFAULT_CLUSTER_CAP comment). 5 matches the
  // value this constant was hardcoded to before it became tunable - no
  // behavior change until a strategy explicitly overrides it. The
  // scripts/aiRoundRobin.mjs round robin found tighter (3) and looser (8)
  // caps both mildly outperform 5 when synergyWeight is on (see
  // synergyWeight's own comment) - not enough evidence to change this
  // default (which only matters when synergyWeight is nonzero anyway), but
  // worth a dedicated sweep before the next synergyWeight attempt.
  clusterCap: 5,
  // Matt: "destroying cards as a strategy" / "consider total cards in the
  // deck" - the same lever really: a bloated deck draws its combo pieces
  // less reliably, and Rest is the AI's only thinning tool. 0 (off)
  // preserves today's exact behavior, where decideTopLevelAction only ever
  // chooses Rest via the hasCurse shortcut - see that function's own
  // comment for the 3-way restructure this enables. Multiplied by the
  // player's total owned card count to score Rest as a genuine competing
  // top-level choice, not just curse cleanup.
  //
  // First pass (a single arbitrary value, 0.05, mixed with the full
  // synergy cluster set): landed close to neutral alone, but combined with
  // synergyWeight was the second-worst strategy in the whole roster,
  // losing even to `easy`. The two knobs interact badly: restBloatWeight
  // scores Rest purely off total owned-card count with no awareness of
  // synergy investment, so a deck that's successfully assembling a combo
  // gets treated as "bloated" and thinned anyway, destroying the pieces
  // synergyWeight just bought.
  //
  // A dedicated category tournament (scripts/rosters/thinning.json - 7
  // values, 0 to 0.4, all-vs-all round robin) sharpened this into a real
  // sweep rather than one guess: win rate declined CLEANLY and
  // monotonically as restBloatWeight rose - 64.3% (0.01) / 63.1% (0.02) /
  // 61.2% (0.05) / 52.0% (0.1) / 36.3% (0.2) / 6.8% (0.4), against a 63.8%
  // baseline. Even the best value (0.01) was statistically tied with doing
  // nothing at all - no restBloatWeight value found so far is a real
  // improvement, only "how close to 0 can it get before it starts
  // actively hurting." This also surfaced a genuine, separate bug worth
  // noting: a high enough value can Rest-destroy a player's entire card
  // collection down to 0 cards over a long game, which used to crash
  // (fixed - see decideTopLevelAction's computeRestScore hand-emptiness
  // guard and playRestTurn's defensive target validation).
  //
  // 0 stays the shipped default - "destroying cards as a strategy," at
  // least via this crude a signal (raw card count, no regard for WHICH
  // cards), is a loser once it does anything more than nothing. A future
  // attempt should score Rest's VALUE, not just deck bloat - e.g. only
  // thin when a specific low-value/off-combo card is identifiable, not on
  // a blanket count threshold.
  restBloatWeight: 0,
  // Which synergyClusters.js cluster ids to score, or null for all of
  // them (today's exact behavior). A plain array (not a Set) so weights
  // objects stay JSON-serializable for scripts/aiTournament.mjs's
  // --weights-a='{...}' CLI flag - converted to a Set only at the point of
  // use (costEfficiency/pickPrepareKeep/playRestTurn). Exists to isolate
  // which SPECIFIC combo family actually helps (e.g. "is the disease chain
  // alone good, independent of the weaker clusters diluting it") -
  // scripts/rosters/synergy-clusters.json builds one strategy per cluster
  // id this way. null here, not an array, so every existing caller that
  // omits it keeps scoring every cluster unchanged.
  activeClusters: null,
  // Matt: "you don't just delete cards in order to win, you delete the
  // cards on the turns where you don't have anything else to do... if a
  // turn can go to the dungeon, it should go to the dungeon... and if
  // none of those are valuable enough, prepare." false (off) preserves
  // decideTopLevelAction's existing scored comparison exactly; true
  // switches to a strict priority order (dungeon if winnable -> village
  // if a buy/level-up is worth it -> rest if hand has a junk card below
  // restJunkThreshold -> prepare) instead of a magnitude comparison -
  // see decideTopLevelActionByPriority's own comment for why this exists:
  // restBloatWeight's scored approach let Rest outbid a perfectly good
  // Dungeon/Village turn just because a bloat-count happened to score
  // higher, which is backwards from how Rest should actually be used.
  //
  // Validated (scripts/rosters/priority-order.json, then a fine-grained
  // restJunkThreshold sweep 1/1.5/2/2.5/3): this fixes the fundamental
  // problem, but doesn't yet produce a decisive win. restJunkThreshold
  // 1.5-2 reaches genuine PARITY with baseline (54.4% vs 54.4% overall,
  // 50.4% head-to-head, consistent across seed windows) - a real
  // improvement in KIND over restBloatWeight (which was a clean loser at
  // every value tested, see its own comment), but not yet a proven
  // improvement in DEGREE over today's default. Values outside that
  // narrow band are clearly worse (never trashing: 47.7%; threshold 2.5+:
  // 42-44%). `priority-hard` (this + hard's validated costFloor/
  // polearm-synergy combo) also lands at parity (50.5% vs baseline), not
  // better. False stays the default - "at least as good, sometimes,
  // within a narrow tuned band" isn't the bar this session has held every
  // other adopted change to. A good target for someone to pick up next:
  // sharpen what "worth it" means for the village-priority step, or widen
  // the fine sweep further around 1.5-2 with a larger sample.
  priorityOrderedActions: false,
  // Only consulted when priorityOrderedActions is true (see its own
  // comment) - the hand's worst handCardKeepScore must fall below this
  // for Rest to be worth choosing at all. 2 sits inside the validated
  // 1.5-2 parity band found above - keep it there if this default is ever
  // flipped on.
  restJunkThreshold: 2,
  // Matt: "let's go ahead and build an actual look ahead system... aim
  // big... make this really good." 0 (off) preserves playAITurn's exact
  // single-step decideTopLevelAction behavior; a positive value switches
  // to playAITurnWithLookahead's real rollout search (see that function's
  // own comment in heuristicBot.js for the full design) - how many
  // simulated turns forward each candidate top-level turn is rolled out
  // before being scored via positionEvaluation.js's evaluatePosition.
  // Validated via scripts/rosters/lookahead-quick.json (depth 1-4 x 5
  // strategies, 120 games/matchup across 3 windows): depth 2 is a clear,
  // dominant sweet spot - 72.2% overall leaderboard win rate, beats depth
  // 1/3/4 head-to-head, beats baseline 85-94% across 3 independent
  // cross-validation windows, and beats DIFFICULTY_PRESETS.hard 96.6% of
  // 60 games. Deeper (3-4) was NOT better, plausibly because rollout
  // continuations use the cheap 0-ply heuristic (rolloutValue forces
  // lookaheadDepth: 0 on every simulated turn beyond the root), which gets
  // less representative of real play the further out it's asked to
  // project. DIFFICULTY_PRESETS.expert uses depth 2 - stays 0 (off) here
  // so every other tier/caller is unaffected.
  lookaheadDepth: 0,
  // How many independent determinized rollout samples to average per
  // candidate (see determinize.js) - only relevant when lookaheadDepth >
  // 0. More samples = a steadier average estimate but proportionally more
  // compute per decision. Swept 1 vs 4 at depth 2 across 5 independent
  // 100-game windows (seed-starts 1/2000/3000/4000/6000, after fixing the
  // dead-pendingHandChoices engine bug samples=4 first surfaced - see
  // actions/index.js's dropDeadEntries comment): 51.5/53.5/52.6/49.5/46.9%,
  // i.e. noise around 50/50 with no consistent direction either way -
  // samples in this range doesn't change which candidate the rollout
  // argmax picks often enough to matter. DIFFICULTY_PRESETS.expert uses 1
  // (cheapest, no measured quality cost) rather than this field's own
  // default of 2, which predates the sweep and is left alone as a
  // reasonable, still-untested-as-wrong value for direct callers.
  lookaheadSamples: 2,
};

// AI difficulty tiers, keyed by the value stored on each AI player's
// `difficulty` field (see createGameState.js) - looked up in store/index.js
// at both playAITurn call sites. Values are empirical, not guessed: found
// via scripts/aiTournament.mjs A-vs-B self-play sweeps (300 games/matchup,
// seats alternated to cancel first-player advantage) run while designing
// this feature -
//   costFloor 1/3/4/6/8 vs. today's default of 2: 1 was clearly worse
//     (38.4% win rate), but 3/4/6/8 were all a real, reproducible
//     improvement (53-55%, well outside the ~5-6pp noise band for 300
//     games) and plateaued from 4 upward - so `hard` uses costFloor: 4,
//     the peak (55.0%).
//   diminishingReturnsRate 0.25/0.75/1.0 vs. default 0.5: 0.25 was much
//     worse (30.3% - confirms variety matters, per DEFAULT_BUY_WEIGHTS'
//     own comment above), 0.75/1.0 were inside the noise band (49.5%/
//     47.6%) - no real improvement direction here, so this knob is left
//     at 0.5 for every tier.
//   mistakeChance 0.15/0.35/0.5/0.7 vs. default 0 (mistakeChance is the
//     only knob that can weaken the bot at all - see its own comment
//     above): a clean, roughly monotonic decline (45.1% / 28.8% / 22.1% /
//     10.1%) - `easy` uses 0.35, landing in the "clearly losing, not a
//     blowout" ~25-35% range.
// Head-to-head, this hard preset beat this easy preset 69.2% of 300 games
// - a real, comfortable difficulty spread (also covered by
// aiDifficulty.test.js's own statistical self-play check).
//
// UPDATE from scripts/aiRoundRobin.mjs's category-tournament series (Matt:
// "get the correct synergy of cards in the correct number... you keep
// your deck thin enough... you will win" - tested directly rather than
// assumed, per his own proposed methodology): synergyWeight/restBloatWeight
// were each swept in isolation (scripts/rosters/synergy-magnitude.json,
// thinning.json), then EVERY individual synergy cluster was tested alone
// (scripts/rosters/synergy-clusters.json, 13 strategies x 78 matchups) to
// find which specific combo family - if any - actually holds up, rather
// than judging the whole mixed bag at once (which earlier this session
// looked neutral-to-negative). Result: 11 of 12 clusters were
// statistically indistinguishable from baseline (noise), but
// `polearm-count` was a real, consistent winner - beat baseline 51.6% and
// beat `hard` (costFloor-only) 51.8%, in both cases 3 of 4 seed windows.
// Confirmed again in a 7-strategy meta round-robin against the full field
// (baseline/hard/easy/best-thinning/all-clusters-synergy) - the two
// polearm-synergy variants tied for first place (56.1% overall, clearly
// ahead of everything else) - and a final spot-check that stacking it
// with costFloor: 4 doesn't cancel either improvement (+2.8pp over
// costFloor-4-alone, +4.3pp over baseline). This is the first synergy-
// related finding this whole investigation that survived every check
// (multiple weight values, multiple seed windows, isolated AND combined
// with other knobs) - `hard` now includes it. `easy`/`normal` are left
// untouched (DEFAULT_BUY_WEIGHTS.synergyWeight/activeClusters stay 0/null)
// - this is a real but modest edge, not dramatic enough to change the
// stable default, matching how costFloor: 4 itself was scoped to `hard`
// only rather than becoming the new default. restBloatWeight (deck-
// thinning) did NOT produce a usable finding on its own - see its own
// comment: only a near-zero value (0.01) avoided being a net negative,
// and even that was statistically tied with doing nothing.
export const DIFFICULTY_PRESETS = {
  easy: { ...DEFAULT_BUY_WEIGHTS, mistakeChance: 0.35 },
  // normal is a literal alias for DEFAULT_BUY_WEIGHTS (one source of
  // truth for "today's shipped behavior"), not a copy.
  normal: DEFAULT_BUY_WEIGHTS,
  // synergyWeight/activeClusters only take effect when
  // state.meta.randomizedVillage is on (see costEfficiency's own gate) -
  // so on the standard curated board, `hard` still plays exactly as it did
  // before this addition (costFloor: 4 only); the polearm-synergy edge
  // documented above only shows up in randomizedVillage games, which is
  // also the only mode it was validated in.
  hard: {
    ...DEFAULT_BUY_WEIGHTS, costFloor: 4, synergyWeight: 1, activeClusters: ['polearm-count'],
  },
  // Matt: "aim big... make this really good" - heuristicBot.js's real
  // multi-turn rollout search (playAITurnWithLookahead), not just another
  // heuristic-weight tweak. An order of magnitude stronger than every
  // other change validated this session (all of which moved win rate by
  // single-digit percentage points at best): beats `hard` 96.6% of 60
  // games. Added as a new top tier rather than folded into `hard` itself -
  // `hard` has an established, comfortably-beatable feel (69.2% vs `easy`
  // over 300 games); overwriting its definition with something that wins
  // 96%+ of the time would be a much bigger behavior change for existing
  // `hard` players than "add a stronger option above it." depth: 2,
  // samples: 1 are the validated values - see DEFAULT_BUY_WEIGHTS'
  // lookaheadDepth/lookaheadSamples comments for the full sweep. Stacks
  // `hard`'s own costFloor/polearm-synergy tuning underneath the search
  // rather than starting from a clean DEFAULT_BUY_WEIGHTS, since nothing
  // found this session suggested lookahead and those knobs conflict, and
  // there was no time-box left to re-validate `expert` without them.
  expert: {
    ...DEFAULT_BUY_WEIGHTS,
    costFloor: 4,
    synergyWeight: 1,
    activeClusters: ['polearm-count'],
    lookaheadDepth: 2,
    lookaheadSamples: 1,
  },
};

// Rough "how good is this card" score. Attack Value is weighted heaviest -
// it's the single lever that actually wins fights, which is what ultimately
// ends the game - followed by VP (what wins on the scoreboard once the game
// ends), then Strength/XP-granted/Light/goldValue as smaller utility terms.
// An earlier version of this heuristic didn't weigh Attack at all, which
// left the buy-picker blind to combat power and caused self-play games to
// stall for thousands of turns never assembling a hand that could win a
// single fight - caught by the AI self-play test never terminating.
// Not card-specific - the same heuristic scores heroes, village cards and
// basics uniformly, which is deliberately simple: individual-card strategy
// is exactly what the "generic fallback" design in the plan expects to be
// good-enough-not-perfect until a card gets bespoke AI heuristics.
// Exported for positionEvaluation.js's "how strong is my whole collection"
// estimate, used by heuristicBot.js's lookahead search - a static-eval
// building block, not just an internal buy-scoring detail anymore.
export function cardValue(def) {
  const attackTraits = parseAlwaysActiveTraits(def.abilityText.alwaysActive);
  const attack = (attackTraits.physicalAttack + attackTraits.magicAttack) * 2.5;
  const vp = (def.points || 0) * 2;
  const power = def.strength || 0;
  // xp is a *cost* (to level up), never a purchase benefit, for any
  // hero-like card - heroes themselves, AND Regular (type: 'basic', but a
  // Level 0 hero-precursor with the identical "xp = leveling cost"
  // semantics via effectHelpers.js's isHeroLike). The old `def.type !==
  // 'hero'` check missed Regular entirely, so its own xp: 2 field was
  // getting counted as +2 of value on top of its Strength 3 and $0 cost -
  // exactly what made it structurally outscore almost everything else.
  const xpValue = isHeroLike(def) ? 0 : (def.xp || 0);
  const utility = (def.light || 0) * 0.5 + (def.goldValue || 0) * 0.25;
  return attack + vp + power + xpValue + utility;
}

// synergy is additive (not multiplicative) so a low-cardValue pure enabler
// can still register a synergy contribution, and so synergyWeight: 0 is an
// exact identity no-op (no stray rounding/NaN risk) - see
// synergyClusters.js's own header for the design rationale.
//
// Gated on state.meta.randomizedVillage: an A/B sweep (yarn ai-tournament
// --randomized-village) found synergyWeight: 0.5 a real, reproducible
// +8.0pp win-rate improvement over 600 games on a fully-randomized board,
// but a real -8.5pp REGRESSION over 300 games on the standard curated
// board - not just a smaller effect, the opposite direction. Makes sense
// in hindsight: the curated fixed hero/village stacks are already
// pre-selected for coherent play, so nudging toward synergy there just
// distracts from picking the individually-strongest card, while a fully-
// randomized board has no such guarantee and benefits from noticing
// whatever weak combo the board actually offers. So this only applies when
// the house rule that actually needs it is on, rather than being folded
// into a difficulty tier that would apply it universally regardless of
// board mode.
function costEfficiency(state, playerId, def, ownedCount, weights) {
  const cost = Math.max(def.cost || 0, weights.costFloor);
  const activeClusterIds = weights.activeClusters ? new Set(weights.activeClusters) : null;
  const synergy = state.meta?.randomizedVillage
    ? synergyScore(state, playerId, def, weights.clusterCap, activeClusterIds) * weights.synergyWeight : 0;
  const raw = (cardValue(def) + synergy) / (cost + 1);
  return raw / (1 + ownedCount * weights.diminishingReturnsRate);
}

function ownedCountOf(state, playerId, defId) {
  return getPlayerCollectionInstanceIds(state, playerId)
    .filter((id) => getInstanceDefinition(state, id).id === defId).length;
}

function topOfStack(state, source) {
  const stack = source.type === 'hero' ? state.village.heroStacks[source.key]
    : source.type === 'village' ? state.village.villageStacks[source.key]
      : state.village.basicStacks[source.key];
  return stack && stack.length ? stack[0] : null;
}

// Every { type, key } buy source with a non-empty stack whose top card this
// player can afford right now - shared by pickBestBuy (which scores and
// ranks these) and heuristicBot.js's pickBestBuyWithMistake (which needs
// the same affordable set to pick a random non-optimal alternative from).
export function listAffordableBuySources(state, playerId) {
  const gold = computeHandGoldValue(state, playerId);
  const sources = [
    ...Object.keys(state.village.heroStacks).map((key) => ({ type: 'hero', key })),
    ...Object.keys(state.village.villageStacks).map((key) => ({ type: 'village', key })),
    ...Object.keys(state.village.basicStacks).map((key) => ({ type: 'basic', key })),
  ];
  return sources.filter((source) => {
    const topInstanceId = topOfStack(state, source);
    if (!topInstanceId) return false;
    const def = getInstanceDefinition(state, topInstanceId);
    return (def.cost || 0) <= gold;
  });
}

// Returns the best affordable { type, key } buy source, or null if nothing
// on the board is worth buying (e.g. everything affordable is a worse-value
// basic than what's already in the deck).
export function pickBestBuy(state, playerId, weights = DEFAULT_BUY_WEIGHTS) {
  let best = null;
  let bestScore = 0.5; // minimum bar - a Regular-tier buy (score ~0) isn't worth spending a turn's buy on
  for (const source of listAffordableBuySources(state, playerId)) {
    const topInstanceId = topOfStack(state, source);
    const def = getInstanceDefinition(state, topInstanceId);
    const ownedCount = ownedCountOf(state, playerId, def.id);
    const score = costEfficiency(state, playerId, def, ownedCount, weights);
    if (score > bestScore) {
      bestScore = score;
      best = source;
    }
  }
  return best;
}

// Best hero (already in hand) worth leveling up this turn, or null.
// NOTE: def.type !== 'hero' also excludes Regulars (type 'basic') here, so
// the AI never considers leveling one up even though the rulebook allows it
// (see engine/village/leveling.js's performLevelUp/
// getRegularLevelUpCandidates, fixed for the human-facing village flow).
// Doing so would also mean picking a target hero group, not just a source
// card - left as a known, deliberate gap rather than a half-considered
// strategy, since the AI just skips heroes it can't level rather than erroring.
export function pickBestLevelUp(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  let best = null;
  let bestScore = -Infinity;
  for (const instanceId of player.hand) {
    const def = getInstanceDefinition(state, instanceId);
    if (def.type !== 'hero' || def.level >= 3) continue;
    if (player.turnFlags.leveledHeroInstanceIds.includes(instanceId)) continue;
    const xpCost = def.xp || 0;
    if (player.xp < xpCost) continue;
    const stack = state.village.heroStacks[def.group] || [];
    if (!stack.some((id) => getInstanceDefinition(state, id).level === def.level + 1)) continue;
    const score = cardValue(def) - xpCost * 0.5; // cheap-in-XP upgrades preferred over expensive ones, all else equal
    if (score > bestScore) {
      bestScore = score;
      best = instanceId;
    }
  }
  return best;
}
